import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  User,
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Building2,
  Shield,
  Clock,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Save,
  X
} from 'lucide-react';

/**
 * UserDetails - View and manage user information
 * Shows user profile, associated businesses, and allows editing
 */
const UserDetails = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  // Fetch user details
  const fetchUser = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setUser(data);
      setEditForm({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || ''
      });
      setBusinesses(data.businesses || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load user details'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Update user
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}`, editForm);
      toast.success('User updated successfully');
      setIsEditing(false);
      fetchUser();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  // Delete user — opens ConfirmModal
  const handleDeleteClick = () => {
    setConfirmModal({
      title: 'Delete User',
      description: `Permanently delete ${user.name || user.email}? This will remove all their data and business associations.`,
      confirmLabel: 'Delete User',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${userId}`);
          toast.success('User deleted successfully');
          navigate('/admin/users');
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete user'));
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="user-details-page">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <h1 className="text-[18px] font-semibold text-slate-800">User Details</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">Loading...</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-light">Loading user details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm px-6 py-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Users
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <p className="text-slate-600 text-[15px] font-medium">User not found</p>
            <p className="text-slate-500 text-[13px] mt-1">The user may have been deleted</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="user-details-page">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/users')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">User Details</h1>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  ID: {userId?.slice(0, 8)}...
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-[13px] font-medium hover:bg-slate-50 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-[13px] font-medium hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-[13px] font-medium hover:bg-slate-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-[#F8F9FB] p-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-rose-200 rounded-xl flex items-center justify-center">
                  <User className="w-8 h-8 text-rose-600" />
                </div>
                <div className="flex-1">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="text-[18px] font-semibold text-slate-800 border-b border-slate-200 focus:border-rose-400 focus:outline-none w-full pb-1"
                      placeholder="Enter name"
                    />
                  ) : (
                    <h2 className="text-[18px] font-semibold text-slate-800">{user.name || 'No Name'}</h2>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${
                      user.is_active !== false
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {user.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Email</p>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="text-[13px] text-slate-700 border-b border-slate-200 focus:border-rose-400 focus:outline-none w-full"
                        placeholder="Enter email"
                      />
                    ) : (
                      <p className="text-[13px] text-slate-700">{user.email || 'N/A'}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Phone</p>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="text-[13px] text-slate-700 border-b border-slate-200 focus:border-rose-400 focus:outline-none w-full"
                        placeholder="Enter phone"
                      />
                    ) : (
                      <p className="text-[13px] text-slate-700">{user.phone || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Associated Businesses */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-rose-500" />
                Associated Businesses
              </h3>

              {businesses.length > 0 ? (
                <div className="space-y-3">
                  {businesses.map((biz, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-[13px] font-medium text-slate-700">{biz.name}</p>
                          <p className="text-[11px] text-slate-500">{biz.email || biz.id}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${
                        biz.role_code === 'OWNER'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {biz.role_code || 'Member'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-[13px]">No associated businesses</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel */}
          <div className="space-y-4">
            {/* Account Info */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-rose-500" />
                Account Info
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-[12px] text-slate-500">User ID</span>
                  <span className="text-[11px] font-mono text-slate-600">{userId?.slice(0, 12)}...</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-[12px] text-slate-500">Status</span>
                  <span className={`flex items-center gap-1 text-[12px] font-medium ${
                    user.is_active !== false ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {user.is_active !== false ? (
                      <><CheckCircle2 className="w-3 h-3" /> Active</>
                    ) : (
                      <><XCircle className="w-3 h-3" /> Inactive</>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-[12px] text-slate-500">Created</span>
                  <span className="text-[12px] text-slate-600">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-[12px] text-slate-500">Last Login</span>
                  <span className="text-[12px] text-slate-600">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-rose-500" />
                Quick Actions
              </h3>

              <div className="space-y-2">
                <button
                  onClick={() => toast.info('Reset Password - integration pending')}
                  className="w-full text-left px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => toast.info('Send Verification Email - integration pending')}
                  className="w-full text-left px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Send Verification Email
                </button>
                <button
                  onClick={() => toast.info('View Activity Log - integration pending')}
                  className="w-full text-left px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  View Activity Log
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={async () => { const fn = confirmModal?.onConfirm; setConfirmModal(null); await fn?.(); }}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default UserDetails;
